import React, { useMemo, useState } from 'react';

/* ============================================================================
   Reusable Zoom-console-style building blocks: page header, tabs, toolbar
   (search + actions), data table, client-side pagination, modal, badges.
   Presentational only — all data + handlers are supplied by the caller, so no
   business logic or API behaviour lives here. Styled by .z-tbl* rules in
   styles/zoom-admin.css (scoped under .zoom-shell, TEC tenant only).
   ============================================================================ */

export interface ZoomColumn {
  key: string;
  label: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (row: any) => React.ReactNode;
}

type ZoomTableProps = {
  title: string;
  subtitle?: string;
  banner?: React.ReactNode;
  tabs?: string[];
  activeTab?: string;
  onTab?: (t: string) => void;
  search?: string;
  onSearch?: (v: string) => void;
  searchPlaceholder?: string;
  actions?: React.ReactNode;
  columns: ZoomColumn[];
  rows: any[];
  rowKey: (row: any) => React.Key;
  rowActions?: (row: any) => React.ReactNode;
  selectable?: boolean;
  emptyText?: string;
  pageSizeOptions?: number[];
  initialPageSize?: number;
};

export function ZoomTable({
  title, subtitle, banner,
  tabs, activeTab, onTab,
  search, onSearch, searchPlaceholder = 'Search',
  actions, columns, rows, rowKey, rowActions,
  selectable = true, emptyText = 'No Data',
  pageSizeOptions = [15, 25, 50, 100], initialPageSize = 15,
}: ZoomTableProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const curPage = Math.min(page, pageCount);
  const pageRows = useMemo(
    () => rows.slice((curPage - 1) * pageSize, curPage * pageSize),
    [rows, curPage, pageSize]
  );

  const colCount = columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0);

  return (
    <div className="z-tbl-page">
      {title ? (
        <div className="z-tbl-titlebar">
          <div>
            <h1 className="z-tbl-title">{title}</h1>
            {subtitle && <div className="z-tbl-subtitle">{subtitle}</div>}
          </div>
        </div>
      ) : null}

      {tabs && tabs.length > 0 && (
        <div className="z-tbl-tabs">
          {tabs.map((t) => (
            <button
              key={t}
              className={`z-tbl-tab${t === activeTab ? ' active' : ''}`}
              onClick={() => onTab && onTab(t)}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {banner && <div className="z-tbl-banner">{banner}</div>}

      <div className="z-tbl-toolbar">
        {onSearch !== undefined ? (
          <div className="z-tbl-search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
            <input
              value={search || ''}
              onChange={(e) => { onSearch(e.target.value); setPage(1); }}
              placeholder={searchPlaceholder}
            />
          </div>
        ) : <div />}
        {actions && <div className="z-tbl-actions">{actions}</div>}
      </div>

      <div className="z-tbl-wrap">
        <table className="z-tbl">
          <thead>
            <tr>
              {selectable && <th className="z-tbl-check"><input type="checkbox" disabled /></th>}
              {columns.map((c) => (
                <th key={c.key} style={{ width: c.width, textAlign: c.align || 'left' }}>{c.label}</th>
              ))}
              {rowActions && <th style={{ textAlign: 'right' }} />}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={colCount}>
                  <div className="z-tbl-empty">
                    <div className="z-tbl-empty-box">📦</div>
                    <div>{emptyText}</div>
                  </div>
                </td>
              </tr>
            ) : (
              pageRows.map((row) => (
                <tr key={rowKey(row)}>
                  {selectable && <td className="z-tbl-check"><input type="checkbox" /></td>}
                  {columns.map((c) => (
                    <td key={c.key} style={{ textAlign: c.align || 'left' }}>
                      {c.render ? c.render(row) : (row[c.key] ?? '—')}
                    </td>
                  ))}
                  {rowActions && <td className="z-tbl-rowactions">{rowActions(row)}</td>}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="z-tbl-footer">
        <div className="z-tbl-pager">
          <button disabled={curPage <= 1} onClick={() => setPage(curPage - 1)}>‹</button>
          <span className="z-tbl-pagenum">{curPage}</span>
          <button disabled={curPage >= pageCount} onClick={() => setPage(curPage + 1)}>›</button>
        </div>
        <select
          className="z-tbl-pagesize"
          value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
        >
          {pageSizeOptions.map((n) => <option key={n} value={n}>{n} per page</option>)}
        </select>
        <span className="z-tbl-count">{total} result{total === 1 ? '' : 's'}</span>
      </div>
    </div>
  );
}

// ── Buttons ──────────────────────────────────────────────────────────────────
export function ZoomBtn({
  children, onClick, variant = 'default', type,
}: { children: React.ReactNode; onClick?: () => void; variant?: 'primary' | 'default' | 'danger'; type?: 'button' | 'submit' }) {
  return (
    <button type={type || 'button'} className={`z-btn z-btn-${variant}`} onClick={onClick}>
      {children}
    </button>
  );
}

// ── Badge ────────────────────────────────────────────────────────────────────
export function ZoomBadge({ text, tone = 'gray' }: { text: string; tone?: 'green' | 'red' | 'blue' | 'gray' | 'amber' }) {
  return <span className={`z-badge z-badge-${tone}`}>{text}</span>;
}

// ── Modal ────────────────────────────────────────────────────────────────────
export function ZoomModal({
  title, onClose, children, footer, width = 560,
}: { title: string; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode; width?: number }) {
  return (
    // Clicking outside does NOT close the window (users lost half-filled forms) — use × or Cancel
    <div className="z-modal-overlay">
      <div className="z-modal" style={{ maxWidth: width }} onClick={(e) => e.stopPropagation()}>
        <div className="z-modal-head">
          <span>{title}</span>
          <button className="z-modal-x" onClick={onClose}>×</button>
        </div>
        <div className="z-modal-body">{children}</div>
        {footer && <div className="z-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

// Simple labelled field for modals
export function ZoomField({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="z-field">
      <span className="z-field-label">{label}</span>
      {children}
      {hint && <span className="z-field-hint">{hint}</span>}
    </label>
  );
}
